import { useNavigate } from "react-router-dom";
import { Bell, Users, Swords, CreditCard, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AppNotification,
  NotificationCategory,
  notificationTitle,
  useNotifications,
} from "@/hooks/useNotifications";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";

const ICONS: Record<NotificationCategory, JSX.Element> = {
  social: <Users className="w-4 h-4 text-primary" />,
  challenge: <Swords className="w-4 h-4 text-accent" />,
  finance: <CreditCard className="w-4 h-4 text-foreground" />,
  system: <Info className="w-4 h-4 text-muted-foreground" />,
};

function NotificationRow({
  n,
  onClick,
}: {
  n: AppNotification;
  onClick: (n: AppNotification) => void;
}) {
  const ago = (() => {
    try {
      return formatDistanceToNow(new Date(n.created_at), { addSuffix: true });
    } catch {
      return "";
    }
  })();

  return (
    <button
      onClick={() => onClick(n)}
      className={cn(
        "w-full text-left px-3 py-2.5 flex gap-2.5 items-start hover:bg-muted/50 transition-colors border-b border-border/50 last:border-b-0",
        !n.read_at && "bg-primary/5"
      )}
    >
      <div className="mt-0.5 shrink-0">{ICONS[n.category] ?? ICONS.system}</div>
      <div className="flex-1 min-w-0">
        <p className={cn("text-sm leading-snug", !n.read_at ? "font-semibold" : "text-foreground")}>
          {notificationTitle(n)}
        </p>
        {ago && <p className="text-[11px] text-muted-foreground mt-0.5">{ago}</p>}
      </div>
      {!n.read_at && (
        <span className="mt-1.5 w-2 h-2 rounded-full bg-primary shrink-0" aria-label="unread" />
      )}
    </button>
  );
}

export default function NotificationsBell() {
  const navigate = useNavigate();
  const { notifications, unreadCount, markAllRead, markRead } = useNotifications();

  const handleClick = (n: AppNotification) => {
    void markRead(n.id);
    if (n.deep_link) navigate(n.deep_link);
  };

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (!open) return;
        // Mark visible notifications as read shortly after open so user gets a beat to see what's new
        setTimeout(() => {
          if (unreadCount > 0) void markAllRead();
        }, 1500);
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-muted-foreground hover:text-foreground h-8 w-8 sm:h-9 sm:w-9"
          title="Notifications"
        >
          <Bell className="w-4 h-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-[min(92vw,360px)] p-0 overflow-hidden"
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-card">
          <p className="text-sm font-semibold">Notifications</p>
          {unreadCount > 0 && (
            <button
              onClick={() => void markAllRead()}
              className="text-[11px] text-primary hover:underline"
            >
              Mark all read
            </button>
          )}
        </div>
        <ScrollArea className="max-h-[60vh]">
          {notifications.length === 0 ? (
            <div className="px-3 py-10 text-center text-sm text-muted-foreground">
              You're all caught up.
            </div>
          ) : (
            notifications.map((n) => <NotificationRow key={n.id} n={n} onClick={handleClick} />)
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
